import React from 'react';
import {Drawer, Row, Col} from 'antd';
import {CloseOutlined} from '@ant-design/icons';
import {RootStoreType} from '@/store/root-store';
import {SitePropertiesDrawer} from '@/components/dashboard/gsc/gsc/sitePropertiesDrawer';
import {getLocalesByKey as t} from '@/utils/translation';
import {SpinnerWrapper} from '@/components/common-components/v2/maps/universalLoader/styled';
import dynamic from 'next/dynamic';
import {NextRouter} from 'next/router';

const DynamicSettingsPage = dynamic(() => import('@/components/dashboard/settings').then(mod => mod.SettingsPage), {loading: () => <SpinnerWrapper/>, ssr: false});
const DynamicPricingSidebar = dynamic(() => import('@/components/dashboard/billing/pricing-sidebar').then(mod => mod.PricingSidebar), {loading: () => <SpinnerWrapper/>, ssr: false});
const DynamicOutDomainsDrawer = dynamic(() => import('@/components/dashboard/backlinks/out_domains').then(mod => mod.OutDomainsDrawer), {loading: () => <SpinnerWrapper/>, ssr: false});
const DynamicCustomerSwitcherModal = dynamic(() => import('@/components/layoutV2/user-menu/customer-switcher/customerSwitcher').then(mod => mod.CustomerSwitcherModal), {loading: () => <SpinnerWrapper/>, ssr: false});
const DynamicOnboardingPromotionModal = dynamic(() => import('@/components/layoutV2/user-menu/onboarding-promotion-modal'), {loading: () => <SpinnerWrapper/>, ssr: false});
const DynamicSettingsModal = dynamic(() => import('@/components/common-components/components/settings-modal'), {loading: () => <SpinnerWrapper/>, ssr: false});

type LayoutModalsAndDrawersProps = {
    store: RootStoreType;
    router: NextRouter;
    isDrawerVisible: boolean;
    onCloseSettingsDrawer: () => void;
    isSettingModalVisible: boolean;
    onCloseSettingsModal: () => void;
    profileCustomerId: number | undefined;
    formatAccountID: (id: number | undefined) => string;
    getSidebarPayment: any;
    outDomainsVisible: boolean;
    showCustomerModal: boolean;
    setShowCustomerModal: (show: boolean) => void;
    showOnboardingPromotionModal: boolean;
    setShowOnboardingPromotionModal: (show: boolean) => void;
    hasOnboardingCompleted: boolean | undefined;
    meetingModalExcludedPages: string[];
    currentPath: string;
};

export const LayoutModalsAndDrawers: React.FC<LayoutModalsAndDrawersProps> = ({
  store,
  isDrawerVisible,
  onCloseSettingsDrawer,
  isSettingModalVisible,
  onCloseSettingsModal,
  profileCustomerId,
  formatAccountID,
  getSidebarPayment,
  outDomainsVisible,
  showCustomerModal,
  setShowCustomerModal,
  showOnboardingPromotionModal,
  setShowOnboardingPromotionModal,
  hasOnboardingCompleted,
  meetingModalExcludedPages,
  currentPath,
}) => {
  return (
    <>
      <SitePropertiesDrawer />

      <Row gutter={20}>
        <Col span={24} style={!isDrawerVisible && {paddingLeft: 0, minHeight: 0}}>
          <Drawer
            width={'88%'}
            title={
              <div className='modal-header'>
                {t('settings')}
                <div className={'account-id-wrapper'}>
                  Account ID:{' '}
                  {formatAccountID(profileCustomerId)}
                </div>
              </div>
            }
            open={isDrawerVisible}
            placement='right'
            contentWrapperStyle={{maxWidth: '900px'}}
            destroyOnClose={true}
            closable={true}
            onClose={onCloseSettingsDrawer}
            closeIcon={<CloseOutlined className={'closeIcon'} style={isDrawerVisible ? {display: 'block'} : {display: 'none'}} />}
            headerStyle={{background: '#F2F2F2', fontSize: '24px', paddingLeft: '30px'}}
            className={'settingsDrawer drawerCloseIcon'}
            bodyStyle={{backgroundColor: '#F2F2F5', padding: '0 30px'}}
          >
            <DynamicSettingsPage store={store} />
          </Drawer>
        </Col>
      </Row>

      {getSidebarPayment && <DynamicPricingSidebar />}
      {outDomainsVisible && <DynamicOutDomainsDrawer />}

      {showCustomerModal && (
        <DynamicCustomerSwitcherModal
          showCustomerModal={showCustomerModal}
          setShowCustomerModal={setShowCustomerModal}
        />
      )}

      {(hasOnboardingCompleted === true && showOnboardingPromotionModal && !meetingModalExcludedPages.includes(currentPath)) && (
        <DynamicOnboardingPromotionModal
          visible={showOnboardingPromotionModal}
          setVisible={setShowOnboardingPromotionModal}
        />
      )}

      {isSettingModalVisible && (
        <DynamicSettingsModal
          visible={isSettingModalVisible}
          onClose={onCloseSettingsModal}
        />
      )}
    </>
  );
};
